import { useCallback } from 'react'
import { useFormikContext } from 'formik'
import dayjs, { Dayjs } from 'dayjs'
import { BasicDatePicker } from 'src/components/shared/form/BasicDatePicker'

interface Props<T> {
  name: keyof T
  label: string
  /** Earliest selectable day. Optional, so existing callers are unaffected. */
  minDate?: Dayjs
  /** Latest selectable day. Optional, so existing callers are unaffected. */
  maxDate?: Dayjs
}

const ISO_DATE_FORMAT = 'YYYY-MM-DD'

/**
 * A `FormikDatePicker`-alike for form values typed as plain ISO date strings.
 *
 * `FormikDatePicker` writes a `Dayjs` object straight into Formik state via `setFieldValue`.
 * `ReviewFormValues` (and the payload mappers / gating logic built on it) types every date as a
 * `string`, so using `FormikDatePicker` directly would put a `Dayjs` object where a string is
 * expected — types would still pass because that field is read back only at runtime (e.g.
 * `blankToUndefined(values.dateIssued)`, `DateTime.fromISO(expiresAt)`), so the break would not
 * show up until then.
 *
 * This wrapper keeps `Dayjs` confined to the `BasicDatePicker` boundary: it reads the ISO string
 * out of Formik, converts it to `Dayjs` only for display, and writes back a formatted ISO string
 * (or `''`) on every change.
 */
export const ReviewDateField = <T extends unknown>({ name, label, minDate, maxDate }: Props<T>) => {
  const { values, touched, errors, setFieldValue } = useFormikContext<T>()

  const isTouched = touched[name] as boolean
  const error = errors[name] as string | undefined

  const rawValue = values[name] as unknown as string
  // `null`, not `undefined`, when empty: `undefined` leaves the MUI X picker uncontrolled, so a
  // later write (the AI "Use these values" prefill) would never show up in the field.
  const value = rawValue ? dayjs(rawValue) : null

  const onChange = useCallback(
    (date: Dayjs | null) => {
      setFieldValue(name as string, date?.format(ISO_DATE_FORMAT) ?? '')
    },
    [name, setFieldValue],
  )

  return (
    <BasicDatePicker
      name={name as string}
      onChange={onChange}
      value={value}
      error={Boolean(isTouched && error)}
      helperText={isTouched && error}
      label={label}
      minDate={minDate}
      maxDate={maxDate}
    />
  )
}
